import React from 'react';
import {ActivityIndicator, Text, View} from 'react-native';
import {useSelector} from 'react-redux';

import {Button} from '../../components/Button';
import {HeatProfileCard} from '../../components/HeatProfileCard';
import {SafeAreaView} from '../../components/SafeAreaView';
import {SectionTitle} from '../../components/SectionTitle';
import {
  SwappableSectionList,
  SwappableSectionListRenderItem,
} from '../../components/SwappableSectionList';
import {SwitchableBackground} from '../../components/SwitchableBackground';
import {Messages, Strings} from '../../constants/Strings';
import {Screens} from '../../constants/navigation';
import {useRealtimeUnitLighting} from '../../contexts/unit-lighting/useRealtimeUnitLight';
import {ChevronLeftIcon} from '../../icons';
import {useAdaptiveSafeArea} from '../../lib/hooks/useAdaptiveSafeArea';
import {useDisconnectGuard} from '../../lib/hooks/useDisconnectGuard';
import {useSaveHeatProfileToDevice} from '../../lib/hooks/useSaveHeatProfileToDevice';
import {useTheme} from '../../lib/hooks/useTheme';
import {currentDeviceHeatProfilesSelector} from '../../lib/redux/slices/bleSlice';
import {allAndCurrentDeviceMoodLightsSelector} from '../../lib/redux/slices/moodLightSlice';
import {heatProfilesMapSelector} from '../../lib/redux/slices/profilesSlice';
import {
  HeatProfile,
  getHeatProfileLight,
  getHeatProfileMoodLight,
} from '../../lib/types';
import styled from '../../lib/utils/styled';
import type {HomeEmulatedDrawerNavigatorScreenProps} from '../../navigators/HomeDrawerNavigator';

export interface Props {
  heatProfileId: string;
}

type ScreenProps = HomeEmulatedDrawerNavigatorScreenProps<
  typeof Screens.SaveToDevice
>;

const getSectionTitle = (key?: string) => {
  switch (key) {
    case 'device':
      return Strings.heatProfiles.sections.device;
    default:
      return '';
  }
};

export const SaveToDeviceScreen = ({route, navigation}: ScreenProps) => {
  useDisconnectGuard();
  const theme = useTheme();
  const {navMenuIconColor, saveToDeviceScreenTheme, spinnerColor} = theme;
  const {background, infoLabelColor} = saveToDeviceScreenTheme;

  const deviceHeatProfiles = useSelector(currentDeviceHeatProfilesSelector);
  const profiles = useSelector(heatProfilesMapSelector);
  const moodLights = useSelector(allAndCurrentDeviceMoodLightsSelector);
  useAdaptiveSafeArea();

  const heatProfileId = route.params.heatProfileId;
  const profile = profiles[heatProfileId];

  useRealtimeUnitLighting(
    Screens.SaveToDevice,
    getHeatProfileLight(moodLights)(profile),
  );

  React.useEffect(() => {
    navigation.setOptions({
      headerLeft: () => (
        <Button variant="transparent" onPress={() => navigation.goBack()}>
          <ChevronLeftIcon
            onPress={() => navigation.goBack()}
            color={navMenuIconColor}
          />
        </Button>
      ),
    });
  }, [navigation, navMenuIconColor]);

  const [{isLoading}, replaceWith] = useSaveHeatProfileToDevice();

  const renderItem: SwappableSectionListRenderItem<HeatProfile> = ({info}) => {
    const {item, index} = info;

    const moodLight = getHeatProfileMoodLight(moodLights)(item);

    return (
      <HeatProfileCard
        moodLight={moodLight}
        theme={theme}
        profile={item}
        {...(!isLoading && {
          onPress: () => replaceWith({index, profile}),
        })}
      />
    );
  };

  const sections = React.useMemo(
    () => [
      {
        key: 'device',
        data: [...deviceHeatProfiles].sort((a, b) => a.order - b.order),
      },
    ],
    [deviceHeatProfiles],
  );

  return (
    <SwitchableBackground background={background}>
      <SafeAreaView
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        {isLoading && <Spinner size={'large'} color={spinnerColor} />}

        <Container>
          <InfoLabel style={{color: infoLabelColor}}>
            {Messages.SAVE_TO_DEVICE_TEXT}
          </InfoLabel>

          <ListContainer>
            <SwappableSectionList
              editingList={false}
              {...{sections}}
              keyExtractor={item => item.id + item.order}
              renderItem={renderItem}
              renderSectionHeader={item => (
                <SectionTitle title={getSectionTitle(item.section.key)} />
              )}
              stickySectionHeadersEnabled={false}
            />
          </ListContainer>
        </Container>
      </SafeAreaView>
    </SwitchableBackground>
  );
};

const Spinner = styled(ActivityIndicator)({
  position: 'absolute',
  zIndex: 1,
});

const Container = styled(View)({
  width: '100%',
  height: '100%',
  flexDirection: 'column',
  alignItems: 'center',
  paddingBottom: 20,
});

const InfoLabel = styled(Text)({
  marginTop: 80,
  marginBottom: 20,
  marginHorizontal: 20,
  fontFamily: 'Roboto-Regular',
  fontWeight: '400',
  fontSize: 12,
  letterSpacing: 0.37,
  lineHeight: 15,
});

const ListContainer = styled(View)({
  width: '100%',
  height: '100%',
  paddingHorizontal: 10,
});
